import { taskNeedsPlanning, type AgentMode, type RunnerMode } from "@project/shared";

// ── Mode action types ──────────────────────────────────────────────────────

export type ModeAction =
  | { type: "noop" }
  | { type: "restart_query"; newMode: AgentMode }
  | { type: "soft_stop" }
  | { type: "start_auto" };

// ── Task context subset needed by ModeController ───────────────────────────

export interface ModeTaskContext {
  status: string;
  plan: string | null;
  storyPointId: string | null;
  isParentTask?: boolean;
  model: string;
  builderModel?: string | null;
  githubPRUrl?: string | null;
}

// ── ModeController: pure state machine with no I/O ─────────────────────────

export class ModeController {
  private _mode: AgentMode;
  private _hasExitedPlanMode = false;
  private _pendingModeRestart = false;
  private _runnerMode: RunnerMode;
  private _isAuto: boolean;

  constructor(initialMode: AgentMode, runnerMode: RunnerMode = "task", isAuto = false) {
    this._mode = initialMode;
    this._runnerMode = runnerMode;
    this._isAuto = isAuto;
  }

  // ── Getters ────────────────────────────────────────────────────────

  get mode(): AgentMode {
    return this._mode;
  }

  get isAuto(): boolean {
    return this._isAuto;
  }

  get hasExitedPlanMode(): boolean {
    return this._hasExitedPlanMode;
  }

  set hasExitedPlanMode(val: boolean) {
    this._hasExitedPlanMode = val;
  }

  get pendingModeRestart(): boolean {
    return this._pendingModeRestart;
  }

  set pendingModeRestart(val: boolean) {
    this._pendingModeRestart = val;
  }

  /** Effective mode accounting for PM/task defaults */
  get effectiveMode(): AgentMode {
    if (this._mode) return this._mode;
    if (this._runnerMode === "pm") {
      return this._isAuto ? "auto" : "discovery";
    }
    return "building";
  }

  get isReadOnly(): boolean {
    const m = this.effectiveMode;
    if (["discovery", "help"].includes(m)) return true;
    return m === "auto" && !this._hasExitedPlanMode;
  }

  get isAutoPlanning(): boolean {
    return this.effectiveMode === "auto" && !this._hasExitedPlanMode;
  }

  get isBuildCapable(): boolean {
    const m = this.effectiveMode;
    return m === "building" || m === "review" || (m === "auto" && this._hasExitedPlanMode);
  }

  /**
   * Apply authoritative mode from the server's task context.
   * Called after getTaskContext to override env-var defaults with the
   * actual task state — ensures pods that launch without CONVEYOR_AGENT_MODE
   * still get the correct mode.
   */
  applyServerMode(agentMode: AgentMode | null | undefined, isAuto: boolean | undefined): void {
    if (agentMode) {
      this._mode = agentMode;
      this._isAuto = agentMode === "auto" || !!isAuto;
    } else if (isAuto !== undefined) {
      this._isAuto = isAuto;
      if (isAuto && this._runnerMode === "pm" && !this._mode) {
        this._mode = "auto";
      }
    }

    // Safety net: discovery + isAuto means the server didn't set agentMode correctly
    if (this._isAuto && this._mode === "discovery") {
      this._mode = "auto";
    }
  }

  // ── Mode resolution ────────────────────────────────────────────────

  /** Resolve the initial mode based on task context */
  resolveInitialMode(context: ModeTaskContext): AgentMode {
    // Code-review runner maps to unified review mode
    if (this._runnerMode === "code-review") {
      this._mode = "review";
      return this._mode;
    }

    // Auto mode: fresh tasks stay in read-only plan mode; only tasks already
    // past the fresh stage (build underway, PR open, restart) bypass planning.
    if (this._mode === "auto" && this.canBypassPlanning(context)) {
      this.transitionToBuilding(context);
      return this._mode;
    }

    return this._mode;
  }

  /**
   * An auto task that still NEEDS PLANNING (no plan saved, pre-build status, no
   * PR — see `taskNeedsPlanning` in @project/shared) runs the read-only plan turn
   * first (`--permission-mode plan`): resolveInitialMode leaves it in `auto` with
   * `_hasExitedPlanMode = false`, and once the agent finishes it calls
   * ExitPlanMode → building. Everything else bypasses planning and builds
   * immediately with `--dangerously-skip-permissions`:
   *
   * - a plan already on the card (e.g. a Planning card planned by a human/PM),
   * - a build already underway (status past `Planning`/`Open` — releases booted
   *   `InProgress`, pod restarts mid-build where DB `agentMode` stays `"auto"`),
   * - a PR already open (safety net if status lags).
   *
   * Runtime Build-after-Discovery never reaches this gate as `auto` — the server's
   * `resolveModeToSend` rewrites it to `"building"` for any task with a plan.
   */
  canBypassPlanning(context: ModeTaskContext): boolean {
    if (this._mode !== "auto") return false;
    return !taskNeedsPlanning(context);
  }

  // ── Mode transitions ───────────────────────────────────────────────

  /** Handle mode change from server */
  handleModeChange(newMode: AgentMode, context?: ModeTaskContext | null): ModeAction {
    if (newMode === this._mode) return { type: "noop" };

    // Task runners can transition to review mode (same-box code review)
    if (this._runnerMode === "task" && newMode === "review") {
      this._mode = newMode;
      return { type: "restart_query", newMode: "review" };
    }

    // Task runners can transition to building mode (server-side auto-advancement)
    if (this._runnerMode === "task" && newMode === "building") {
      this._mode = newMode;
      this._isAuto = true;
      this._hasExitedPlanMode = true;
      return { type: "restart_query", newMode: "building" };
    }

    // Task runners can transition to auto mode (e.g., Build button after discovery).
    // A card that still needs planning (no plan saved yet) keeps the read-only
    // plan turn instead of jumping straight to building; without context we
    // preserve the legacy build-immediately behavior.
    if (this._runnerMode === "task" && newMode === "auto") {
      this._mode = newMode;
      this._isAuto = true;
      this._hasExitedPlanMode = !context || !taskNeedsPlanning(context);
      return { type: "restart_query", newMode: "auto" };
    }

    // Already building in auto mode — server confirmed via wakeAgentForBuilding.
    // Update mode label but don't restart the running query.
    if (this._mode === "auto" && this._hasExitedPlanMode && newMode === "building") {
      this._mode = newMode;
      return { type: "noop" };
    }

    if (this._runnerMode !== "pm") return { type: "noop" };

    this._mode = newMode;
    this.updateExitedPlanModeFlag(newMode);

    if (this.isBuildCapable) {
      return { type: "start_auto" };
    }
    return { type: "noop" };
  }

  /** Handle ExitPlanMode call from the agent */
  handleExitPlanMode(context: ModeTaskContext): ModeAction {
    if (this._hasExitedPlanMode) return { type: "noop" };
    this.transitionToBuilding(context);
    this._pendingModeRestart = true;
    return { type: "restart_query", newMode: this._mode };
  }

  /** Check if pack runner behavior should be used */
  isPackRunner(context: ModeTaskContext): boolean {
    return !!context.isParentTask;
  }

  // ── Internal helpers ───────────────────────────────────────────────

  private transitionToBuilding(context: ModeTaskContext): void {
    this._hasExitedPlanMode = true;
    this._mode = context.isParentTask ? "review" : "building";
  }

  private updateExitedPlanModeFlag(newMode: AgentMode): void {
    if (newMode === "building") {
      this._hasExitedPlanMode = true;
    }
    // For auto mode, we can't set the flag without context — it gets
    // set during context fetch or when ExitPlanMode is called
  }
}
